import React, { useState, useRef, useEffect } from 'react';
import { CameraIcon, XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';

interface PhotoStripSectionProps {
  playImages: File[];
  existingImages: string[];
  onPlayImagesChange: (images: File[]) => void;
  onDeleteExistingImage?: (imageUrl: string) => void;
  readOnly?: boolean;
  maxPhotos?: number;
}

const PhotoStripSection: React.FC<PhotoStripSectionProps> = ({
  playImages,
  existingImages,
  onPlayImagesChange,
  onDeleteExistingImage,
  readOnly = false,
  maxPhotos = 3,
}) => {
  const [showCamera, setShowCamera] = useState(false);
  const [activeSlot, setActiveSlot] = useState<number | null>(null);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Create and clean up object URLs for new images
  useEffect(() => {
    const urls = playImages.map(file => URL.createObjectURL(file));
    setPreviewUrls(urls);
    return () => {
      urls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [playImages]);

  const totalImages = existingImages.length + playImages.length;

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: 1920, height: 1080 },
        audio: false,
      });

      streamRef.current = stream;
      setShowCamera(true);

      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (err) {
      console.error('Error accessing camera:', err);
      // Try front camera if back camera fails
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: 1920, height: 1080 },
          audio: false,
        });

        streamRef.current = stream;
        setShowCamera(true);

        setTimeout(() => {
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
        }, 100);
      } catch (fallbackErr) {
        console.error('Error accessing any camera:', fallbackErr);
        alert('Camera access denied. Please allow camera permissions and try again.');
      }
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      if (context) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const file = new File([blob], `play-photo-${Date.now()}.jpg`, {
                type: 'image/jpeg',
              });
              onPlayImagesChange([...playImages, file]);
              stopCamera();
            }
          },
          'image/jpeg',
          0.8
        );
      }
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
    setActiveSlot(null);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const remainingSlots = maxPhotos - totalImages;

    if (files.length > remainingSlots) {
      alert(`You can only add ${remainingSlots} more photo${remainingSlots !== 1 ? 's' : ''}.`);
      return;
    }

    onPlayImagesChange([...playImages, ...files.slice(0, remainingSlots)]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setActiveSlot(null);
  };

  const handleSlotClick = (index: number) => {
    if (readOnly || totalImages >= maxPhotos) return;
    setActiveSlot(index);
  };

  const handleRemoveNewImage = (index: number) => {
    const newImages = playImages.filter((_, i) => i !== index);
    onPlayImagesChange(newImages);
  };

  const renderSlot = (index: number) => {
    const existingImageIndex = index;
    const newImageIndex = index - existingImages.length;

    // Existing image
    if (index < existingImages.length) {
      return (
        <div key={`existing-${index}`} className="relative group">
          <div className="w-20 h-14 rounded-lg overflow-hidden border-2 border-edge shadow-sm">
            <img
              src={existingImages[existingImageIndex]}
              alt={`Play session ${index + 1}`}
              className="w-full h-full object-cover"
            />
          </div>
          {!readOnly && onDeleteExistingImage && (
            <button
              type="button"
              onClick={() => onDeleteExistingImage(existingImages[existingImageIndex])}
              className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
            >
              <XMarkIcon className="w-3 h-3" />
            </button>
          )}
        </div>
      );
    }

    // New image
    if (newImageIndex >= 0 && newImageIndex < playImages.length) {
      return (
        <div key={`new-${newImageIndex}`} className="relative group">
          <div className="w-20 h-14 rounded-lg overflow-hidden border-2 border-primary-300 shadow-sm">
            <img
              src={previewUrls[newImageIndex] || ''}
              alt={`New upload ${newImageIndex + 1}`}
              className="w-full h-full object-cover"
            />
          </div>
          {!readOnly && (
            <button
              type="button"
              onClick={() => handleRemoveNewImage(newImageIndex)}
              className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
            >
              <XMarkIcon className="w-3 h-3" />
            </button>
          )}
        </div>
      );
    }

    // Empty slot
    return (
      <button
        key={`empty-${index}`}
        type="button"
        onClick={() => handleSlotClick(index)}
        disabled={readOnly}
        data-testid={`add-photos-button-${index}`}
        className={`w-20 h-14 rounded-lg border-2 border-dashed flex items-center justify-center transition-all ${
          readOnly
            ? 'border-edge bg-surface-base cursor-not-allowed'
            : 'border-edge-input hover:border-indigo-400 hover:bg-indigo-50 cursor-pointer'
        }`}
      >
        <CameraIcon className={`w-6 h-6 ${readOnly ? 'text-faint' : 'text-disabled'}`} />
      </button>
    );
  };

  return (
    <div>
      <label className="block text-sm font-medium text-body mb-3">
        <span className="flex items-center">
          <CameraIcon className="w-4 h-4 mr-1.5 text-indigo-500" />
          Photos
          <span className="text-disabled font-normal ml-1">
            ({totalImages}/{maxPhotos})
          </span>
        </span>
      </label>

      {/* Photo slots */}
      <div className="flex items-center justify-center space-x-4">
        {Array.from({ length: maxPhotos }).map((_, index) => renderSlot(index))}
      </div>

      {/* Action menu when slot is clicked */}
      {activeSlot !== null && !showCamera && (
        <div className="mt-3 flex space-x-2">
          <button
            type="button"
            onClick={startCamera}
            className="flex-1 flex items-center justify-center px-3 py-2 border border-edge-input rounded-lg shadow-sm bg-surface text-sm font-medium text-body hover:bg-surface-hover transition-colors"
          >
            <CameraIcon className="w-4 h-4 mr-2 text-indigo-500" />
            Take Photo
          </button>
          <label className="flex-1 flex items-center justify-center px-3 py-2 border border-edge-input rounded-lg shadow-sm bg-surface text-sm font-medium text-body hover:bg-surface-hover cursor-pointer transition-colors">
            <PlusIcon className="w-4 h-4 mr-2 text-indigo-500" />
            Choose File
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
          </label>
          <button
            type="button"
            onClick={() => setActiveSlot(null)}
            className="px-3 py-2 text-disabled hover:text-body"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Camera interface */}
      {showCamera && (
        <div className="mt-3">
          <div className="relative bg-black rounded-lg overflow-hidden">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-48 object-cover"
            />
            <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex space-x-3">
              <button
                type="button"
                onClick={capturePhoto}
                className="bg-surface text-heading rounded-full p-3 shadow-lg hover:bg-surface-active transition-all"
              >
                <CameraIcon className="w-6 h-6" />
              </button>
              <button
                type="button"
                onClick={stopCamera}
                className="bg-red-500 text-white rounded-full px-4 py-2 shadow-lg hover:bg-red-600 transition-all text-sm font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
          <canvas ref={canvasRef} className="hidden" />
        </div>
      )}
    </div>
  );
};

export default PhotoStripSection;
